import { useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { getLocale, t } from "@/lib/i18n"
import { Spinner, Stub } from "@/components/ui"
import type { MemoryOut, ProfileFactOut } from "@/lib/types"

export const Route = createFileRoute("/_app/brain")({
  component: BrainPage,
})

/**
 * The Brain as Lucy's notebook: what she knows about you, in words.
 * Topics (one per memory kind) on the left, your profile facts as chips,
 * memories as cards you can read, correct, or tell her to forget.
 */

const KIND_LABELS: Record<string, { el: string; en: string }> = {
  communication_style: { el: "Τρόπος επικοινωνίας", en: "How you talk" },
  preference: { el: "Προτιμήσεις", en: "Preferences" },
  project: { el: "Έργα", en: "Projects" },
  fact: { el: "Γεγονότα", en: "Facts" },
  event: { el: "Συμβάντα", en: "Events" },
  relationship: { el: "Σχέσεις", en: "Relationships" },
  sensitivity: { el: "Ευαισθησίες", en: "Sensitivities" },
  routine_pattern: { el: "Ρουτίνες", en: "Routines" },
  nudge_style: { el: "Υπενθυμίσεις", en: "Nudges" },
}

function kindLabel(kind: string): string {
  const l = KIND_LABELS[kind]
  if (!l) return kind.replace(/_/g, " ")
  return getLocale() === "el" ? l.el : l.en
}

function BrainPage() {
  const me = auth.me()
  const qc = useQueryClient()
  const el = getLocale() === "el"
  const [topic, setTopic] = useState<string | null>(null)
  const [q, setQ] = useState("")
  const [sort, setSort] = useState<"recent" | "important">("recent")
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)

  const userDetail = useQuery({
    queryKey: ["user-detail", me?.id],
    queryFn: () => api<{ profile_facts: ProfileFactOut[] }>(`/api/dashboard/users/${me?.id}`),
    enabled: !!me?.id,
  })
  const memories = useQuery({
    queryKey: ["memories-brain", me?.id],
    // Every memory, in pages: the notebook is the whole brain, not a sample.
    queryFn: async () => {
      const page = 1000
      const all: MemoryOut[] = []
      for (let offset = 0; ; offset += page) {
        const rows = await api<MemoryOut[]>(`/api/dashboard/users/${me?.id}/memories`, {
          query: { limit: page, offset },
        })
        all.push(...rows)
        if (rows.length < page) break
      }
      return all
    },
    enabled: !!me?.id,
  })

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { text?: string; keep?: boolean } }) =>
      api<MemoryOut>(`/api/dashboard/memories/${id}`, { method: "PATCH", body }),
    onSuccess: () => { setEditing(null); void qc.invalidateQueries({ queryKey: ["memories-brain", me?.id] }) },
  })
  const forget = useMutation({
    mutationFn: (id: string) => api<unknown>(`/api/dashboard/memories/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memories-brain", me?.id] }),
  })

  const all = memories.data ?? []
  const topics = useMemo(() => {
    const m = new Map<string, number>()
    for (const x of all) m.set(x.kind, (m.get(x.kind) ?? 0) + 1)
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [all])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    let rows = all
    if (topic) rows = rows.filter((x) => x.kind === topic)
    if (needle) rows = rows.filter((x) => x.text.toLowerCase().includes(needle))
    rows = [...rows].sort((a, b) =>
      sort === "important"
        ? b.salience - a.salience || Date.parse(b.created_at) - Date.parse(a.created_at)
        : Date.parse(b.created_at) - Date.parse(a.created_at),
    )
    return rows
  }, [all, topic, q, sort])

  const facts = userDetail.data?.profile_facts ?? []
  const loading = userDetail.isLoading || memories.isLoading
  const empty = !loading && all.length === 0 && facts.length === 0
  const dateFmt = new Intl.DateTimeFormat(el ? "el-GR" : "en-GB", { day: "numeric", month: "short" })

  return (
    <div className="-m-4 md:-m-8 flex min-h-[calc(100dvh-52px)] md:min-h-[100dvh] flex-col md:flex-row">
      {/* Topics */}
      <aside className="flex shrink-0 flex-col gap-5 border-b border-border bg-card/40 px-5 py-6 md:sticky md:top-0 md:h-[100dvh] md:w-[280px] md:overflow-y-auto md:border-b-0 md:border-r md:px-6 md:py-8">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[30px] leading-none">{t("brain.title")}</h1>
          <p className="text-[13px] leading-relaxed text-muted-foreground">{t("brain.sub")}</p>
        </div>
        <label className="flex h-10 items-center gap-2 rounded-xl bg-card px-3 text-muted-foreground ring-1 ring-inset ring-border focus-within:ring-primary/50">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("brain.search")}
            className="w-full bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>
        <nav className="flex flex-row flex-wrap gap-1 md:flex-col md:gap-0.5">
          <TopicRow active={topic === null} label={t("brain.all")} count={all.length} onClick={() => setTopic(null)} />
          {topics.map(([kind, count]) => (
            <TopicRow key={kind} active={topic === kind} label={kindLabel(kind)} count={count} onClick={() => setTopic(kind)} />
          ))}
        </nav>
        <div className="mt-auto hidden flex-col gap-1 rounded-xl bg-card p-3.5 ring-1 ring-inset ring-border md:flex">
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t("brain.total")}</div>
          <div className="text-[22px] font-display leading-none">
            {all.length} <span className="font-sans text-[13px] font-normal text-muted-foreground">{t("brain.memories")} · {facts.length} facts</span>
          </div>
        </div>
      </aside>

      {/* Notebook */}
      <section className="flex min-w-0 flex-1 flex-col gap-5 px-5 py-6 md:px-8 md:py-8">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("brain.loading")}</div>
        )}
        {empty && (
          <Stub title={t("brain.emptyTitle")} hint={t("brain.emptyHint")} />
        )}
        {!loading && !empty && (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-3">
                <div className="font-display text-[24px] leading-none">{topic ? kindLabel(topic) : t("brain.all")}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                  {shown.length} {t("brain.memories")}
                </div>
              </div>
              <div className="flex gap-1.5">
                <SortPill active={sort === "recent"} onClick={() => setSort("recent")}>{t("brain.recent")}</SortPill>
                <SortPill active={sort === "important"} onClick={() => setSort("important")}>{t("brain.important")}</SortPill>
              </div>
            </div>

            {!topic && facts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {facts.map((f) => (
                  <span key={f.key} className="inline-flex h-8 items-center gap-2 rounded-full bg-mint/10 px-3 text-[13px] text-foreground ring-1 ring-inset ring-mint/35">
                    <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-mint">{f.key.replace(/_/g, " ")}</span>
                    {f.value}
                  </span>
                ))}
              </div>
            )}

            {shown.length === 0 ? (
              <div className="text-sm text-muted-foreground">{t("brain.noMatch")}</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {shown.slice(0, 300).map((m, i) => {
                  const age = Math.min(1, Math.max(0, (Date.now() - Date.parse(m.created_at)) / 86_400_000 / 180))
                  const dots = m.salience >= 0.75 ? 3 : m.salience >= 0.5 ? 2 : 1
                  const isEditing = editing?.id === m.id
                  return (
                    <article
                      key={m.id}
                      className="flex flex-col gap-2.5 rounded-2xl bg-card p-4 ring-1 ring-inset ring-border"
                      style={{ opacity: 1 - age * 0.35 }}
                    >
                      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                        <span>{dateFmt.format(new Date(m.created_at))}{topic ? "" : ` · ${kindLabel(m.kind)}`}</span>
                        <span className="inline-flex gap-[3px]" title={`salience ${(m.salience * 100).toFixed(0)}%`}>
                          {[1, 2, 3].map((n) => (
                            <span key={n} className={`h-1.5 w-1.5 rounded-full ${n <= dots ? "bg-violet" : "bg-border"}`} />
                          ))}
                        </span>
                      </div>
                      {isEditing ? (
                        <textarea
                          value={editing.text}
                          onChange={(e) => setEditing({ id: m.id, text: e.target.value })}
                          rows={4}
                          className="w-full rounded-xl bg-background p-2.5 text-[14px] leading-snug text-foreground ring-1 ring-inset ring-border outline-none focus:ring-primary/50"
                        />
                      ) : (
                        <p className={`${i === 0 && sort === "important" ? "text-[17px]" : "text-[15px]"} leading-snug text-foreground`}>{m.text}</p>
                      )}
                      {(m.reason || m.source_conversation_id) && !isEditing && (
                        <div className="flex flex-col gap-1 text-[12px] text-muted-foreground">
                          {m.reason && <div className="italic">{t("brain.why")}: {m.reason}</div>}
                          {m.source_conversation_id && (
                            <Link to="/chat" search={{ c: m.source_conversation_id }} className="inline-flex items-center gap-1 text-violet hover:underline">
                              {t("brain.fromConversation")} →
                            </Link>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={() => patch.mutate({ id: m.id, body: { text: editing.text.trim(), keep: true } })}
                              disabled={patch.isPending || !editing.text.trim()}
                              className="font-medium text-primary hover:underline disabled:opacity-50"
                            >
                              {t("brain.save")}
                            </button>
                            <button type="button" onClick={() => setEditing(null)} className="hover:text-foreground">{t("brain.cancel")}</button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => setEditing({ id: m.id, text: m.text })} className="hover:text-foreground">{t("brain.correct")}</button>
                            <button
                              type="button"
                              onClick={() => { if (window.confirm(t("brain.forgetConfirm"))) forget.mutate(m.id) }}
                              disabled={forget.isPending}
                              className="hover:text-destructive"
                            >
                              {t("brain.forget")}
                            </button>
                          </>
                        )}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
            {shown.length > 300 && (
              <div className="text-xs text-muted-foreground">{t("brain.more", { n: shown.length - 300 })}</div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

function TopicRow({ active, label, count, onClick }: { active: boolean; label: string; count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[38px] items-center justify-between gap-3 rounded-xl px-3 text-[14px] transition md:w-full ${
        active ? "bg-secondary text-foreground ring-1 ring-inset ring-border" : "text-foreground/80 hover:bg-secondary/60"
      }`}
    >
      <span className="inline-flex items-center gap-2.5">
        <span className={`h-2 w-2 rounded-full ${active ? "bg-primary" : "bg-violet"}`} />
        {label}
      </span>
      <span className="font-mono text-[11px] text-muted-foreground">{count}</span>
    </button>
  )
}

function SortPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-full px-3.5 text-[13px] font-medium transition ${
        active ? "bg-primary text-primary-foreground" : "bg-card text-foreground ring-1 ring-inset ring-border hover:bg-secondary"
      }`}
    >
      {children}
    </button>
  )
}
